import { useEffect, useState, useRef } from 'react'
import {
  CardContent,
  CardHeader,
  Card,
  Button,
  Alert,
  CardActions,
  Grid,
  Box,
  List,
  ListItemText,
  ListItemButton,
  useTheme,
  Typography,
  AlertTitle
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Device, Product, Valve } from '../../types'
import { getDevice } from '../../services/deviceService'
import { QrReader } from 'react-qr-reader'
import DeviceCodeInput from '../DeviceCodeInput'
import { QrCode } from '@mui/icons-material'
import { DeviceType } from '../../enums'
import DeviceDetachAlert from '../DeviceDetachAlert'
import ValveDetachHeaterAlert from '../ValveDetachHeaterAlert'
import { usePolicy } from '../../hooks'
import { extractCompleteShortCode } from '../../utils/deviceCode'
import { sanitizeShortCodeInput, SHORT_CODE_LENGTH } from '../../utils/deviceCode'

interface ScanQrCodeOrEnterDeviceCodeStepProps {
  type?: DeviceType
  product?: Product
  withDetachAlert?: boolean
  afterDetach?: (device: Device) => void
  withDetachHeaterAlert?: boolean
  initialDeviceName?: string
  onChange: (deviceName: string) => void
  onCancel: () => void
  cancelButtonLabel?: string
  onBack?: () => void
  onlineRequired?: boolean
  onNext: () => void
}

export default function ScanQrCodeOrEnterDeviceCodeStep({
  type,
  product,
  withDetachAlert = true,
  afterDetach,
  withDetachHeaterAlert = true,
  initialDeviceName,
  onChange,
  onCancel,
  cancelButtonLabel,
  onBack,
  onlineRequired = false,
  onNext
}: ScanQrCodeOrEnterDeviceCodeStepProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('installation')

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The policy state.
   */
  const { isSuperadmin, isManufacturer } = usePolicy()

  /**
   * Indicates if the QR-reader is enabled.
   */
  const [showQrReader, setShowQrReader] = useState<boolean>(false)

  /**
   * The short UUID of the device.
   */
  const [shortUUID, setShortUUID] = useState<string>('')
  const lastRequestedShortUUIDRef = useRef<string>('')
  const autoAdvanceDeviceRef = useRef<string | null>(null)

  /**
   * The error after loading the device.
   */
  const [deviceError, setDeviceError] = useState<Error>()

  /**
   * The selected device.
   */
  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>()

  /**
   * Indicates if the detach heater alert should be shown.
   */
  const [showDetachHeaterAlert, setShowDetachHeaterAlert] = useState<boolean>(withDetachHeaterAlert)

  /**
   * Indicates if the alert for the wrong type of device should be shown.
   */
  const [showTypeMismatchErrorAlert, setShowTypeMismatchErrorAlert] = useState<boolean>(false)

  const [showProductMismatchErrorAlert, setShowProductMismatchErrorAlert] = useState<boolean>(false)

  /**
   * Indicates if the device loaded with an error.
   */
  const isDeviceError = deviceError !== undefined

  const shouldShowOfflineWarning =
    onlineRequired && selectedDevice !== undefined && selectedDevice.status === false

  /**
   * Dismiss the detach heater alert.
   */
  function dismissDetachHeaterAlert() {
    setShowDetachHeaterAlert(false)
  }

  /**
   * Load the device by short UUID.
   */
  const loadDevice = async (shortUUID: string, controller?: AbortController) => {
    try {
      setDeviceError(undefined)
      setShowTypeMismatchErrorAlert(false)
      setShowProductMismatchErrorAlert(false)

      lastRequestedShortUUIDRef.current = shortUUID

      const device = await getDevice(shortUUID, { signal: controller?.signal })

      if (!device) {
        throw new Error('device-not-found')
      }

      if (type && device.type !== type) {
        setShowTypeMismatchErrorAlert(true)
      } else if (product && device.product && device.product.id !== product.id) {
        setShowProductMismatchErrorAlert(true)
      } else {
        setSelectedDevice(device)
        if (!onlineRequired || device.status) {
          onChange(device.name)
        }
      }
    } catch (error: unknown) {
      setDeviceError(error as Error)
      lastRequestedShortUUIDRef.current = ''
    }
  }

  const handleCancel = () => {
    setDeviceError(undefined)
    setSelectedDevice(undefined)
    setShortUUID('')
    lastRequestedShortUUIDRef.current = ''
    autoAdvanceDeviceRef.current = null
    onCancel()
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    }
  }

  const handleNext = () => {
    onNext()
  }

  useEffect(() => {
    if (!selectedDevice) {
      autoAdvanceDeviceRef.current = null
      return
    }

    if (onlineRequired && selectedDevice.status === false) {
      return
    }

    if (withDetachAlert && selectedDevice.asset?.projectId) {
      return
    }

    const requiresHeaterDetach =
      withDetachHeaterAlert &&
      showDetachHeaterAlert &&
      selectedDevice.type === DeviceType.VALVE &&
      selectedDevice.heaterId !== null

    if (requiresHeaterDetach) {
      return
    }

    if (autoAdvanceDeviceRef.current === selectedDevice.name) {
      return
    }

    autoAdvanceDeviceRef.current = selectedDevice.name
    onNext()
  }, [
    selectedDevice,
    onlineRequired,
    withDetachAlert,
    withDetachHeaterAlert,
    showDetachHeaterAlert,
    onNext
  ])

  /**
   * Load the device when short UUID changes.
   */
  useEffect(() => {
    if (shortUUID.length !== SHORT_CODE_LENGTH) {
      return
    }

    if (lastRequestedShortUUIDRef.current === shortUUID) {
      return
    }

    const controller = new AbortController()
    loadDevice(shortUUID, controller)

    return () => controller.abort()
  }, [shortUUID])

  useEffect(() => {
    if (initialDeviceName && selectedDevice === undefined) {
      const controller = new AbortController()

      loadDevice(initialDeviceName, controller)

      return () => controller.abort()
    }

    return undefined
  }, [initialDeviceName, selectedDevice])

  return (
    <Card>
      <CardHeader title={t('scanQrCodeOrEnterDeviceCode')} />
      <CardContent sx={{ pt: 0 }}>
        <Grid container spacing={6}>
          {isDeviceError && (
            <Grid item xs={12}>
              <Alert severity="error">
                <AlertTitle sx={{ m: 0 }}>{t('deviceWasNotFound')}</AlertTitle>
              </Alert>
            </Grid>
          )}

          {showTypeMismatchErrorAlert && (
            <Grid item xs={12}>
              <Alert severity="error">
                <AlertTitle sx={{ mt: 0 }}>{t('deviceWasFoundButTypeWasMismatch')}</AlertTitle>
              </Alert>
            </Grid>
          )}

          {showProductMismatchErrorAlert && (
            <Grid item xs={12}>
              <Alert severity="error">
                <AlertTitle sx={{ mt: 0 }}>
                  {t('deviceWasFoundButAttachedToAnotherProduct')}
                </AlertTitle>
              </Alert>
            </Grid>
          )}

          {selectedDevice === undefined ? (
            <Grid
              item
              xs={12}
              sx={{
                textAlign: 'center'
              }}
            >
              {!showQrReader ? (
                <Box
                  p={6}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    minHeight: '25vh',
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <QrCode sx={{ fontSize: 64 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {t('installation:enableCameraToReadQrCode')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setShowQrReader(true)}
                      >
                        {t('installation:enableCamera')}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Box sx={{ width: '100%', maxWidth: 320 }}>
                    <QrReader
                      scanDelay={500}
                      constraints={{ facingMode: 'environment', aspectRatio: 9 / 16 }}
                      onResult={(result) => {
                        if (result) {
                          const completeCode = extractCompleteShortCode(result.getText())
                          if (completeCode) {
                            setShortUUID(completeCode)
                          }
                        }
                      }}
                      containerStyle={{ width: '100%' }}
                      videoContainerStyle={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '177.78%',
                        backgroundColor: 'black',
                        borderRadius: 8,
                        overflow: 'hidden'
                      }}
                      videoStyle={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                </Box>
              )}

              <DeviceCodeInput
                value={shortUUID}
                onChange={(value) => {
                  const sanitized = sanitizeShortCodeInput(value)
                  lastRequestedShortUUIDRef.current = ''
                  setDeviceError(undefined)
                  setShortUUID(sanitized)

                  if (sanitized.length !== SHORT_CODE_LENGTH) {
                    return
                  }
                }}
              />

              {isDeviceError && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  <AlertTitle sx={{ m: 0 }}>{t('deviceWasNotFound')}</AlertTitle>
                </Alert>
              )}
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sx={{ mt: 2 }}>
                {shouldShowOfflineWarning ? (
                  <Alert severity="warning">
                    <AlertTitle sx={{ mt: 0 }}>{t('common:deviceIsOffline')}</AlertTitle>
                    {t('deviceMustBeOnlineToContinue')}
                  </Alert>
                ) : (
                  <Alert severity="success">
                    <AlertTitle sx={{ mt: 0 }}>{t('deviceWasFound')}</AlertTitle>
                  </Alert>
                )}

                <List>
                  {selectedDevice?.asset?.name &&
                    selectedDevice.asset.name !== selectedDevice.name && (
                      <ListItemButton>
                        <ListItemText
                          primary={selectedDevice.asset.name}
                          secondary={t('asset:name')}
                        />
                      </ListItemButton>
                    )}

                  {selectedDevice?.asset?.company?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.asset.company.name}
                        secondary={t('asset:company')}
                      />
                    </ListItemButton>
                  )}

                  {selectedDevice?.asset?.project?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.asset.project.name}
                        secondary={t('asset:project')}
                      />
                    </ListItemButton>
                  )}

                  {selectedDevice?.asset?.level?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.asset.level.name}
                        secondary={t('asset:level')}
                      />
                    </ListItemButton>
                  )}

                  {selectedDevice?.asset?.lift?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.asset.lift.name}
                        secondary={t('asset:lift')}
                      />
                    </ListItemButton>
                  )}

                  <ListItemButton>
                    <ListItemText primary={selectedDevice.name} secondary={t('device:name')} />
                  </ListItemButton>

                  <ListItemButton>
                    <ListItemText
                      primary={selectedDevice.shortUUID}
                      secondary={t('device:shortUUID')}
                    />
                  </ListItemButton>

                  {(isSuperadmin || isManufacturer) && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.simId ?? '-'}
                        secondary={t('device:simId')}
                      />
                    </ListItemButton>
                  )}

                  <ListItemButton>
                    <ListItemText
                      primary={selectedDevice.firmwareVersion ?? '-'}
                      secondary={t('device:firmwareVersion')}
                    />
                  </ListItemButton>

                  <ListItemButton>
                    <ListItemText
                      primary={selectedDevice.hardwareVersion ?? '-'}
                      secondary={t('device:hardwareVersion')}
                    />
                  </ListItemButton>
                </List>

                {withDetachAlert && selectedDevice?.asset?.projectId && (
                  <DeviceDetachAlert device={selectedDevice} afterDetach={afterDetach} />
                )}
              </Grid>

              {withDetachHeaterAlert &&
                showDetachHeaterAlert &&
                selectedDevice.type === DeviceType.VALVE &&
                selectedDevice.heaterId !== null && (
                  <Grid item xs={12}>
                    <ValveDetachHeaterAlert
                      valve={selectedDevice as Valve}
                      onCancel={dismissDetachHeaterAlert}
                      afterUpdate={setSelectedDevice}
                    />
                  </Grid>
                )}
            </>
          )}
        </Grid>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        {onBack && (
          <Button variant="text" color="inherit" onClick={handleBack}>
            {t('installation:back')}
          </Button>
        )}

        <Button variant="text" color="inherit" onClick={handleCancel}>
          {cancelButtonLabel ?? t('cancel')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={
            selectedDevice === undefined || (onlineRequired && selectedDevice.status === false)
          }
          onClick={handleNext}
        >
          {t('next')}
        </Button>
      </CardActions>
    </Card>
  )
}
